import { Autocomplete, FormControl, FormHelperText } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import * as yup from "yup";

import { Controller, useForm } from "react-hook-form";
import { AddMemberBody, ErrorFieldResponse } from "../../constants/types";
import { useAuth } from "../../context/authContext";

import { yupResolver } from "@hookform/resolvers/yup";
import { Close } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import defaultAxios, { AxiosError } from "axios";
import moment from "moment";
import "moment/locale/id";
import { useMutation, useQueryClient } from "react-query";
import axios from "../../services/axios";
import toRupiah from "../../utils/toRupiah";

import { pdf } from "@react-pdf/renderer";
import { validRegions } from "../../constants/common";
import useLanggananMember from "../../services/queries/useLanggananMember";
import useMetodePembayaran from "../../services/queries/useMetodePembayaran";
import useUkm from "../../services/queries/useUkm";
import useUser from "../../services/queries/useUser";
import { getLocalUkmId } from "../../utils/handleChangeUkmId";
import LicensePlateInput from "../LicensePlateInput/LicensePlateInput";
import PrintStrukMember from "../PrintStrukMember/PrintStrukMember";

moment.locales();
moment.locale("id");

interface IModalPelangganProps {
    isOpenModalMember: boolean;
    closeModalMember: () => void;
}

interface Body {
    ukmId: number | undefined;
    namaMember: string;
    perusahaan: string;
    noPolisi: string;
    jenislanggananId: number;
    startLangganan: string;
    endLangganan: string;
    metodeId: number;
}

interface PrintBody {
    ukmId: number | undefined;
    namaMember: string;
    perusahaan: string;
    noPolisi: string;
    jenislanggananId: number;
    startLangganan: string;
    endLangganan: string;
    metodeId: number;
    namaMetode: string;
}

const schema = yup.object().shape({
    namaMember: yup.string().required("Nama member wajib diisi"),
    perusahaan: yup.string().required("Nama perusahaan wajib diisi"),
    noPolisi: yup
        .string()
        .required("Nomor polisi wajib diisi")
        .test("format", "Format tidak valid", (value) => {
            if (!value) return false;

            const fullFormatRegex = /^[A-Z]{1,2}\s\d{1,4}\s[A-Z]{1,3}$/;

            return fullFormatRegex.test(value);
        })
        .test("region", "Kode area tidak valid", (value) => {
            if (!value) return false;

            const region = value.split(" ")[0];
            return validRegions.includes(region);
        })
        .test("number-range", "Angka harus 1-4 digit", (value) => {
            if (!value) return false;

            const parts = value.split(" ");
            if (parts.length >= 2) {
                const number = parseInt(parts[1]);
                return number >= 1 && number <= 9999;
            }
            return true;
        })
        .test("letter-suffix", "Huruf belakang harus 1-3 karakter", (value) => {
            if (!value) return false;

            const parts = value.split(" ");
            if (parts.length === 3) {
                const suffix = parts[2];
                return suffix.length >= 1 && suffix.length <= 3;
            }
            return false;
        })
        .max(11, "Nomor polisi terlalu panjang"),
    jenislanggananId: yup
        .number()
        .test(
            "required",
            "Jenis langganan wajib dipilih",
            (value) => value !== 0 && value !== undefined && value !== null,
        ),
    endLangganan: yup.date().required("Tanggal akhir langganan wajib diisi"),
});

const ModalPelanggan = ({
    isOpenModalMember,
    closeModalMember,
}: IModalPelangganProps) => {
    const { ukmIdUser } = useAuth();
    const { data: user } = useUser();
    const [successData, setSuccessData] = useState<PrintBody | null>(null);

    let now = moment().format("YYYY-MM-DD");
    let nowMember = moment().format("DD MMM YYYY");

    const initialParamsUkm = {
        size: 500,
        page: 1,
        search: "",
    };
    const { data: ukm } = useUkm(initialParamsUkm);

    const ukmId = useMemo(() => {
        if (user?.userId) {
            return getLocalUkmId(user?.userId);
        }
        return null;
    }, [user?.userId]);

    const ukmData = useMemo(() => {
        if (ukmId) {
            return ukm?.content.find((li) => li.id === ukmId);
        }
        return ukm?.content[0];
    }, [ukm?.content, ukmId]);

    const initialParamsLanggananMember = {
        ukmId: ukmIdUser && ukmIdUser[0],
    };

    const { data: langgananMember } = useLanggananMember(
        initialParamsLanggananMember,
    );

    const { data: metodePembayaran, isLoading: isLoadingMetodePembayaran } =
        useMetodePembayaran(ukmId);

    useEffect(() => {
        if (!metodePembayaran) return;
        if (metodePembayaran.filter((item) => item.id === 0).length > 0) return;
        metodePembayaran?.unshift({
            id: 0,
            ukmId: { id: 0, nama: "", isWebstore: 0 },
            namaMetode: "Tunai",
            noTujuan: "",
            namaPemegang: "",
            gambar: "",
            isShowWebstore: 0,
            isBayarParkir: 0,
        });
    }, [metodePembayaran]);

    const initialValues = useMemo(
        () => ({
            namaMember: "",
            perusahaan: "",
            noPolisi: "",
            jenislanggananId: 0,
            endLangganan: new Date(),
            metodePembayaran: {
                id: 0,
                ukmId: { id: 0, nama: "" },
                namaMetode: "Tunai",
                noTujuan: "",
                namaPemegang: "",
                gambar: "",
            },
        }),
        [],
    );

    const { handleSubmit, control, reset, setError, setValue, watch } =
        useForm<AddMemberBody>({
            resolver: yupResolver(schema),
            defaultValues: initialValues,
        });
    const watchJenisLanggananId = watch("jenislanggananId");
    const watchEndLangganan = watch("endLangganan");

    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (values: Body) => {
            const res = await axios.post("/api/members", values);
            return res.data;
        },
    });

    const handleJenisLanggananChange = (value: number) => {
        const selectedSubscription = langgananMember?.content.find(
            (item) => item.id === value,
        );

        if (selectedSubscription) {
            let endDate = new Date();

            if (selectedSubscription.totalHari?.includes("+1 months")) {
                endDate.setMonth(endDate.getMonth() + 1);
            } else if (selectedSubscription.totalHari?.includes("+3 months")) {
                endDate.setMonth(endDate.getMonth() + 3);
            } else if (selectedSubscription.totalHari?.includes("+1 year")) {
                endDate.setFullYear(endDate.getFullYear() + 1);
            } else if (selectedSubscription.totalHari?.includes("+5 year")) {
                endDate.setFullYear(endDate.getFullYear() + 5);
            }

            setValue("endLangganan", endDate);
        }
    };

    const onSubmit = async (values: AddMemberBody) => {
        const body = {
            ukmId: ukmIdUser && ukmIdUser[0],
            namaMember: values.namaMember,
            perusahaan: values.perusahaan,
            noPolisi: values.noPolisi,
            jenislanggananId: values.jenislanggananId,
            startLangganan: now,
            endLangganan: moment(values.endLangganan).format("YYYY-MM-DD"),
            metodeId: values.metodePembayaran.id,
            charge: values.metodePembayaran?.charge?.charge ?? null,
            tipeCharge: values.metodePembayaran?.charge?.mode ?? null,
        };
        const bodyWithNamaMetode = {
            ukmId: ukmIdUser && ukmIdUser[0],
            namaMember: values.namaMember,
            perusahaan: values.perusahaan,
            noPolisi: values.noPolisi,
            jenislanggananId: values.jenislanggananId,
            startLangganan: now,
            endLangganan: moment(values.endLangganan).format("YYYY-MM-DD"),
            metodeId: values.metodePembayaran.id,
            namaMetode: values.metodePembayaran.namaMetode,
        };
        console.log(body);
        mutation.mutate(body, {
            onSuccess: () => {
                setSuccessData(bodyWithNamaMetode);
                queryClient.invalidateQueries("list-members");
                Swal.fire({
                    title: "Member berhasil ditambahkan",
                    position: "top-end",
                    showConfirmButton: false,
                    icon: "success",
                    toast: true,
                    timer: 3000,
                    timerProgressBar: true,
                    showCloseButton: true,
                    customClass: {
                        container: "my-swal",
                    },
                });
                closeModalMember();
                reset(initialValues);
            },
            onError: (error) => {
                if (defaultAxios.isAxiosError(error)) {
                    const serverError = error as AxiosError<
                        ErrorFieldResponse | undefined
                    >;
                    if (serverError && serverError?.response) {
                        const fieldError = serverError?.response?.data;
                        if (fieldError?.errors) {
                            Object.keys(fieldError.errors).forEach((key) => {
                                const errorMessage = fieldError.errors[key];
                                setError(key as keyof typeof initialValues, {
                                    type: "manual",
                                    message: errorMessage[0],
                                });
                            });
                        } else {
                            Swal.fire({
                                title: `${
                                    fieldError?.message ?? "Terjadi Kesalahan!"
                                }`,
                                position: "top-end",
                                showConfirmButton: false,
                                icon: "error",
                                toast: true,
                                timer: 3000,
                                timerProgressBar: true,
                                showCloseButton: true,
                                customClass: {
                                    container: "my-swal",
                                },
                            });
                        }
                    }
                }
                console.error(error);
            },
        });
    };

    const handlePrintStruk = async () => {
        if (!successData || !ukmData || !langgananMember) return;

        try {
            const blob = await pdf(
                <PrintStrukMember
                    namaToko={String(ukmData.namaToko)}
                    alamatToko={String(ukmData.alamatToko)}
                    gambarToko={
                        ukmData.gambarTokoBW === null
                            ? undefined
                            : ukmData.gambarTokoBW
                    }
                    telpToko={String(ukmData.noTelp)}
                    tanggalTransaksi={nowMember}
                    isPro={Number(ukmData.isPro)}
                    namaMember={successData.namaMember}
                    perusahaan={successData.perusahaan}
                    noPolisi={successData.noPolisi}
                    jenisLangganan={
                        langgananMember.content.find(
                            (item) => item.id === successData.jenislanggananId,
                        )?.nama || ""
                    }
                    nominalLangganan={
                        (langgananMember.content.find(
                            (item) => item.id === successData.jenislanggananId,
                        )?.nominal as string) || ""
                    }
                    endLangganan={successData.endLangganan ?? ""}
                    namaMetode={successData.namaMetode}
                />,
            ).toBlob();

            const pdfUrl = URL.createObjectURL(blob);
            window.open(pdfUrl);
            setSuccessData(null);
        } catch (error) {
            console.error("Error generating PDF:", error);
        }
    };

    useEffect(() => {
        if (successData) {
            handlePrintStruk();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [successData]);

    useEffect(() => {
        if (isOpenModalMember) {
            reset(initialValues);
        }
    }, [isOpenModalMember, initialValues, reset]);

    return (
        <Dialog
            maxWidth="md"
            fullWidth={true}
            open={isOpenModalMember}
            onClose={closeModalMember}
            scroll="body"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle sx={{ padding: 4.5 }}>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent={"space-between"}
                >
                    <Typography variant="h5" fontWeight="bold">
                        Tambah Member
                    </Typography>
                    <IconButton onClick={closeModalMember}>
                        <Close />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ padding: 4.5 }}>
                <Grid
                    container
                    spacing={2}
                    alignItems="flex-start"
                    marginBottom={2}
                >
                    <Grid item xs={12}>
                        <Typography
                            fontWeight={500}
                            component="div"
                            marginBottom={1}
                        >
                            Nama Member
                        </Typography>
                        <Controller
                            name={"namaMember"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    id="namaMember"
                                    placeholder="Masukkan nama member"
                                    fullWidth
                                    variant="outlined"
                                    size="medium"
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                        },
                                    }}
                                    error={!!fieldState?.error}
                                    helperText={fieldState?.error?.message}
                                    inputProps={{ maxLength: 50 }}
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    spacing={2}
                    alignItems="flex-start"
                    marginBottom={2}
                >
                    <Grid item xs={12}>
                        <Typography
                            fontWeight={500}
                            component="div"
                            marginBottom={1}
                        >
                            Perusahaan
                        </Typography>
                        <Controller
                            name={"perusahaan"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    id="perusahaan"
                                    placeholder="Masukkan nama perusahaan"
                                    fullWidth
                                    variant="outlined"
                                    size="medium"
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                        },
                                    }}
                                    error={!!fieldState?.error}
                                    helperText={fieldState?.error?.message}
                                    inputProps={{ maxLength: 50 }}
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    spacing={2}
                    alignItems="flex-start"
                    marginBottom={2}
                >
                    <Grid item xs={12}>
                        <Typography
                            fontWeight={500}
                            component="div"
                            marginBottom={1}
                        >
                            Nomor Polisi
                        </Typography>
                        <Controller
                            name={"noPolisi"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <LicensePlateInput
                                    id="noPolisi"
                                    placeholder="Masukkan nomor polisi"
                                    fullWidth
                                    variant="outlined"
                                    size="medium"
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                        },
                                    }}
                                    error={!!fieldState?.error}
                                    helperText={fieldState?.error?.message}
                                    inputProps={{ maxLength: 50 }}
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12}>
                        <Typography
                            fontWeight={500}
                            component="div"
                            marginBottom={1}
                        >
                            Jenis Langganan
                        </Typography>
                        <Controller
                            name="jenislanggananId"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormControl
                                    size="medium"
                                    fullWidth
                                    error={!!fieldState?.error}
                                >
                                    <Select
                                        labelId="demo-simple-select-helper-label"
                                        id="demo-simple-select-helper"
                                        placeholder="Pilih Jenis Langganan"
                                        value={
                                            field.value === 0 ? "" : field.value
                                        }
                                        onChange={(e) => {
                                            const value = e.target
                                                .value as number;
                                            field.onChange(value);
                                            handleJenisLanggananChange(value);
                                        }}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            Pilih Jenis Langganan
                                        </MenuItem>
                                        {langgananMember &&
                                            langgananMember.content.map((k) => (
                                                <MenuItem
                                                    key={k.id}
                                                    value={k.id}
                                                >
                                                    {k.nama} -{" "}
                                                    {toRupiah(
                                                        Number(k.nominal),
                                                    )}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                    {fieldState?.error && (
                                        <FormHelperText error>
                                            {fieldState.error.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            )}
                        />
                        <Stack
                            direction="row"
                            justifyContent="flex-end"
                            visibility={
                                watchJenisLanggananId && watchEndLangganan
                                    ? "visible"
                                    : "hidden"
                            }
                        >
                            <Typography
                                display="inline-block"
                                component="div"
                                color="text.secondary"
                                marginTop={1}
                                textAlign="right"
                            >
                                Berlaku sampai{" "}
                                <Typography display="inline" fontWeight={500}>
                                    {moment(watchEndLangganan).format(
                                        "DD MMM YYYY",
                                    )}
                                </Typography>
                            </Typography>
                        </Stack>
                    </Grid>
                </Grid>
                <Grid
                    container
                    spacing={2}
                    alignItems="flex-start"
                    marginBottom={2}
                >
                    <Grid item xs={12}>
                        <Typography
                            fontWeight={500}
                            component="div"
                            marginBottom={1}
                        >
                            Metode Pembayaran
                        </Typography>
                        <Controller
                            name="metodePembayaran"
                            control={control}
                            render={({ field, fieldState }) => (
                                <Autocomplete
                                    disablePortal
                                    disableClearable
                                    loading={isLoadingMetodePembayaran}
                                    options={metodePembayaran ?? []}
                                    getOptionLabel={(option) =>
                                        option.namaMetode
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        String(option.id) === String(value.id)
                                    }
                                    value={field.value}
                                    onChange={(_event, newOption) => {
                                        field.onChange(newOption);
                                    }}
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Pilih Metode Pembayaran"
                                            error={!!fieldState?.error}
                                            helperText={
                                                fieldState?.error
                                                    ? "Pilih satu metode pembayaran"
                                                    : ""
                                            }
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                </Grid>

                <Stack
                    direction="row"
                    spacing={1.5}
                    width="100%"
                    mt={4}
                    sx={{
                        justifyContent: "flex-end",
                        alignItems: "center",
                    }}
                >
                    <LoadingButton
                        loading={mutation.isLoading}
                        loadingPosition="center"
                        variant="outlined"
                        onClick={closeModalMember}
                    >
                        Kembali
                    </LoadingButton>

                    <LoadingButton
                        variant="contained"
                        loading={mutation.isLoading}
                        loadingPosition="center"
                        onClick={handleSubmit(onSubmit)}
                    >
                        Tambahkan
                    </LoadingButton>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default ModalPelanggan;
